import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, staggerContainer } from '../data/constants';

interface ContactFormData {
    name: string;
    email: string;
    phone?: string;
    subject: string;
    message: string;
    inquiryType: 'general' | 'courses' | 'technical' | 'partnership';
}

const Contact: React.FC = () => {
    const [formData, setFormData] = useState<ContactFormData>({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        inquiryType: 'general'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errors, setErrors] = useState<Partial<ContactFormData>>({});

    const inquiryTypes = [
        { id: 'general', name: 'General Inquiry' },
        { id: 'courses', name: 'Course Information' },
        { id: 'technical', name: 'Technical Support' },
        { id: 'partnership', name: 'Partnership/Business' }
    ];

    const contactMethods = [
        {
            icon: 'email',
            title: 'Email Us',
            value: 'hello@swarsadhana.com',
            description: 'Send us an email anytime',
            action: 'mailto:hello@swarsadhana.com'
        },
        {
            icon: 'phone',
            title: 'Call Us',
            value: '+91 88806 25790',
            description: 'Mon-Fri from 9am to 6pm IST',
            action: 'tel:+918880625790'
        },
        {
            icon: 'chat',
            title: 'Live Chat',
            value: 'Chat with us',
            description: 'Available 24/7 for instant help',
            action: '#'
        },
        {
            icon: 'location',
            title: 'Visit Us',
            value: 'Mumbai, India',
            description: 'Schedule an in-person meeting',
            action: '#'
        }
    ];

    const validateForm = (): boolean => {
        const newErrors: Partial<ContactFormData> = {};

        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
        }
        if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
        if (!formData.message.trim()) newErrors.message = 'Message is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Store locally for demo
            localStorage.setItem('contactSubmission', JSON.stringify({
                ...formData,
                timestamp: new Date().toISOString()
            }));

            setIsSuccess(true);
            setFormData({
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: '',
                inquiryType: 'general'
            });
        } catch (error) {
            console.error('Contact form error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInputChange = (field: keyof ContactFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const iconMap: { [key: string]: JSX.Element } = {
        email: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
        ),
        phone: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
        ),
        chat: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
        ),
        location: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
        )
    };

    return (
        <section id="contact" className="py-20 bg-gray-50 relative overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute inset-0 z-0">
                <motion.div
                    animate={{
                        rotate: [0, 360],
                        scale: [1, 1.1, 1],
                    }}
                    transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                    className="absolute top-20 right-20 w-32 h-32 border-2 border-brand-gold/10 rounded-full"
                />
                <motion.div
                    animate={{
                        rotate: [360, 0],
                        scale: [1, 0.9, 1],
                    }}
                    transition={{
                        duration: 15,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                    className="absolute bottom-20 left-20 w-24 h-24 border-2 border-brand-dark-red/10 rounded-full"
                />
            </div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Section Header */}
                <motion.div
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    className="text-center mb-16"
                >
                    <motion.h2
                        variants={fadeInUp}
                        className="font-cinzel text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
                    >
                        Get in <span className="text-brand-dark-red">Touch</span>
                    </motion.h2>
                    <motion.p
                        variants={fadeInUp}
                        className="font-poppins text-xl text-gray-600 max-w-3xl mx-auto"
                    >
                        Have questions about our courses? Need help getting started? We're here to help you begin your musical and wellness journey.
                    </motion.p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    {/* Contact Methods */}
                    <motion.div
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="space-y-8"
                    >
                        <motion.div variants={fadeInUp}>
                            <h3 className="font-cinzel text-2xl font-bold text-gray-900 mb-6">
                                Contact Information
                            </h3>
                            <p className="font-poppins text-gray-600 mb-8">
                                Choose the most convenient way to reach us. Our team is ready to assist you with any questions about our courses, scheduling, or technical support.
                            </p>
                        </motion.div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {contactMethods.map((method) => (
                                <motion.a
                                    key={method.title}
                                    variants={fadeInUp}
                                    href={method.action}
                                    onClick={method.icon === 'chat' ? (e) => {
                                        e.preventDefault();
                                        // Trigger chatbot
                                        console.log('Open chatbot');
                                    } : undefined}
                                    whileHover={{
                                        scale: 1.02,
                                        y: -5,
                                        boxShadow: "0 20px 40px rgba(0,0,0,0.1)"
                                    }}
                                    whileTap={{ scale: 0.98 }}
                                    className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer border border-gray-100 hover:border-brand-dark-red/20"
                                >
                                    <div className="flex flex-col space-y-4">
                                        {/* Icon and Title Row */}
                                        <div className="flex items-center space-x-3">
                                            <motion.div
                                                className="w-12 h-12 bg-brand-dark-red rounded-lg flex items-center justify-center text-white group-hover:bg-brand-gold group-hover:text-gray-900 transition-colors duration-300 flex-shrink-0"
                                                whileHover={{ rotate: 360 }}
                                                transition={{ duration: 0.6 }}
                                            >
                                                {iconMap[method.icon]}
                                            </motion.div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-poppins font-semibold text-gray-900 text-lg group-hover:text-brand-dark-red transition-colors duration-300">
                                                    {method.title}
                                                </h4>
                                            </div>
                                        </div>

                                        {/* Contact Value */}
                                        <div className="pl-0">
                                            <p className="font-poppins text-brand-dark-red font-semibold text-base mb-2 break-words group-hover:text-brand-gold transition-colors duration-300">
                                                {method.value}
                                            </p>
                                            <p className="font-poppins text-sm text-gray-600 leading-relaxed">
                                                {method.description}
                                            </p>
                                        </div>
                                    </div>
                                </motion.a>
                            ))}
                        </div>

                        {/* FAQ Quick Links */}
                        <motion.div variants={fadeInUp} className="bg-white rounded-xl p-6 shadow-lg">
                            <h4 className="font-cinzel text-lg font-bold text-gray-900 mb-4">
                                Quick Help
                            </h4>
                            <div className="space-y-3">
                                <a href="#" className="block font-poppins text-brand-dark-red hover:text-brand-gold transition-colors">
                                    → How to book your first class
                                </a>
                                <a href="#" className="block font-poppins text-brand-dark-red hover:text-brand-gold transition-colors">
                                    → Technical requirements for online classes
                                </a>
                                <a href="#" className="block font-poppins text-brand-dark-red hover:text-brand-gold transition-colors">
                                    → Pricing and payment options
                                </a>
                                <a href="#" className="block font-poppins text-brand-dark-red hover:text-brand-gold transition-colors">
                                    → Cancellation and rescheduling policy
                                </a>
                            </div>
                        </motion.div>
                    </motion.div>

                    {/* Contact Form */}
                    <motion.div
                        variants={fadeInUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="bg-white rounded-2xl shadow-xl p-8"
                    >
                        {isSuccess ? (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <h3 className="font-cinzel text-xl font-bold text-gray-900 mb-2">
                                    Message Sent Successfully!
                                </h3>
                                <p className="font-poppins text-gray-600 mb-6">
                                    Thank you for reaching out. We'll get back to you within 24 hours.
                                </p>
                                <button
                                    onClick={() => setIsSuccess(false)}
                                    className="bg-brand-gold text-gray-900 px-6 py-2 rounded-lg font-poppins font-semibold hover:bg-yellow-500 transition-colors"
                                >
                                    Send Another Message
                                </button>
                            </div>
                        ) : (
                            <>
                                <h3 className="font-cinzel text-2xl font-bold text-gray-900 mb-6">
                                    Send us a Message
                                </h3>

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Name */}
                                        <div>
                                            <label className="block font-poppins font-medium text-gray-700 mb-2">
                                                Full Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.name}
                                                onChange={(e) => handleInputChange('name', e.target.value)}
                                                className={`w-full px-4 py-3 border rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                                                placeholder="Your full name"
                                                disabled={isSubmitting}
                                            />
                                            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                                        </div>

                                        {/* Email */}
                                        <div>
                                            <label className="block font-poppins font-medium text-gray-700 mb-2">
                                                Email Address *
                                            </label>
                                            <input
                                                type="email"
                                                value={formData.email}
                                                onChange={(e) => handleInputChange('email', e.target.value)}
                                                className={`w-full px-4 py-3 border rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                                                placeholder="your@email.com"
                                                disabled={isSubmitting}
                                            />
                                            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Phone */}
                                        <div>
                                            <label className="block font-poppins font-medium text-gray-700 mb-2">
                                                Phone Number (Optional)
                                            </label>
                                            <input
                                                type="tel"
                                                value={formData.phone || ''}
                                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all"
                                                placeholder="+91 9876543210"
                                                disabled={isSubmitting}
                                            />
                                        </div>

                                        {/* Inquiry Type */}
                                        <div>
                                            <label className="block font-poppins font-medium text-gray-700 mb-2">
                                                Inquiry Type
                                            </label>
                                            <select
                                                value={formData.inquiryType}
                                                onChange={(e) => handleInputChange('inquiryType', e.target.value as any)}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all"
                                                disabled={isSubmitting}
                                            >
                                                {inquiryTypes.map(type => (
                                                    <option key={type.id} value={type.id}>{type.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Subject */}
                                    <div>
                                        <label className="block font-poppins font-medium text-gray-700 mb-2">
                                            Subject *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.subject}
                                            onChange={(e) => handleInputChange('subject', e.target.value)}
                                            className={`w-full px-4 py-3 border rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all ${errors.subject ? 'border-red-500' : 'border-gray-300'}`}
                                            placeholder="What can we help you with?"
                                            disabled={isSubmitting}
                                        />
                                        {errors.subject && <p className="text-red-500 text-sm mt-1">{errors.subject}</p>}
                                    </div>

                                    {/* Message */}
                                    <div>
                                        <label className="block font-poppins font-medium text-gray-700 mb-2">
                                            Message *
                                        </label>
                                        <textarea
                                            value={formData.message}
                                            onChange={(e) => handleInputChange('message', e.target.value)}
                                            className={`w-full px-4 py-3 border rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all resize-none ${errors.message ? 'border-red-500' : 'border-gray-300'}`}
                                            rows={5}
                                            placeholder="Please provide details about your inquiry..."
                                            disabled={isSubmitting}
                                        />
                                        {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
                                    </div>

                                    {/* Submit Button */}
                                    <motion.button
                                        type="submit"
                                        whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                                        whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                                        disabled={isSubmitting}
                                        className={`w-full py-3 rounded-lg font-poppins font-semibold text-base transition-all ${isSubmitting
                                            ? 'bg-gray-400 cursor-not-allowed text-white'
                                            : 'bg-brand-dark-red hover:bg-red-800 text-white shadow-lg hover:shadow-xl'
                                            }`}
                                    >
                                        {isSubmitting ? (
                                            <div className="flex items-center justify-center">
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                Sending Message...
                                            </div>
                                        ) : (
                                            'Send Message'
                                        )}
                                    </motion.button>
                                </form>
                            </>
                        )}
                    </motion.div>
                </div>
            </div>

            {/* Chatbot Widget Placeholder */}
            <div className="fixed bottom-6 right-6 z-40">
                <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="w-14 h-14 bg-brand-dark-red rounded-full shadow-lg flex items-center justify-center text-white hover:bg-brand-gold hover:text-gray-900 transition-all duration-300"
                    onClick={() => console.log('Open chatbot')}
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                </motion.button>
            </div>
        </section>
    );
};

export default Contact; 
